<template>
  <cargo-ag-grid-wrapper
    :config-module="columnDefs.length > 0 ? configModule : ''"
    :grid-options="gridOptions"
    :column-defs="columnDefs"
    :row-data="finalRowData"
    :show-row-groups="true"
    :class="'dw-ag-grid' + (finalRowData.length > 150 ? ' loanding-renderer' : '')"
    :filter-menu-items="filterMenuItems"
    :row-group-default-collapse="true"
    @sort-changed="onSortChanged"
    @columnRowGroupChanged="onColumnRowGroupChanged"
  />
</template>

<script>
import cargoAgGridWrapper from '@/components/Cargo/Common/cargoAgGridWrapper'
import { formatNumberToThousands, getLength } from '@/utils'
import ColumnUtils from '@/utils/ColumnUtils'
import lodash from 'lodash'

// 26字母
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

export default {
  name: 'DwAgGrid',
  components: { cargoAgGridWrapper },
  props: {
    configModule: { type: String, default: '' },
    /*
    [...
      {
        dataType: 'string', // 数据类型
        display: false, // 是否显示
        group: false, // 是否分组
        name: 'name', // 字段名称
        label: 'label', // 标题名称
        precision: 0, // 精度
        subSum: false // 是否可小计
      }
    ...]
    */
    headers: { type: Array, required: true },
    rowData: { type: Array, required: true },
    cellClassName: { type: Function, default: null },
    subSumMaxNum: { type: Number, default: 2 }, // 小计的最大列数
    subSumDisiplayAll: { type: Boolean, default: true }, // 小计行显示所有数据
    showSum: { type: Boolean, default: true }, // 是否显示总计
    filterMenuItems: { type: Function, default: null } // 过滤菜单项
  },
  data() {
    return {
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true, // 如果为true，则不能选择单元格。这意味着当您单击它们时，单元格不会获得键盘焦点。
        suppressRowClickSelection: true, // 如果为true， 则单击行时不会发生行选择。当您想要专门选择复选框时使用。
        suppressRowTransform: true,
        context: {
          componentParent: this
        }
      },
      columnDefs: [],
      sortList: [],
      sortHeaders: [],
      sortTypes: [],
      sortHeader: null,
      sortType: '',
      sortedRowData: [],
      statisticsRows: [], // 统计行
      hasRowGroup: false // 是否有行分组
    }
  },
  computed: {
    displayHeaders() {
      return this.headers.filter(p => p.display)
    },
    finalRowData() {
      return this.hasRowGroup ? this.rowData : this.sortedRowData
    }
  },
  watch: {
    displayHeaders() {
      this.resetColumnDefs()
      this.toGroup()
      this.toStatistics()
      this.onSortChanged()
    },
    rowData() {
      this.toGroup()
      this.toStatistics()
      this.onSortChanged()
    },
    subSumMaxNum() {
      this.toStatistics()
      this.onSortChanged()
    },
    showSum() {
      this.toStatistics()
      this.onSortChanged()
    },
    finalRowData() {
      // 重置列宽
      const columnStates = this.gridOptions.columnApi.getColumnState()
      const headerMap = new Map()
      for (const header of this.displayHeaders) {
        headerMap.set(header.name, header)
      }
      for (const columnState of columnStates) {
        if (columnState.colId === '_seq') {
          continue
        }
        const header = headerMap.get(columnState.colId)
        if (!header) {
          continue
        }
        columnState.width = this.getColumnTextWidth(header)
      }
      this.gridOptions.columnApi.setColumnState(columnStates)
    }
  },
  created() {
    this.resetColumnDefs()
    this.toGroup()
    this.toStatistics()
    this.onSortChanged()
  },
  methods: {
    resetColumnDefs() {
      this.columnDefs = []
      if (!this.hasRowGroup) {
        this.columnDefs.push({
          headerName: '',
          field: '_seq',
          width: this.getColumnTextWidth({ name: '_seq' }),
          sortable: false,
          suppressMenu: true,
          headerClass: 'ag-header-yellow',
          cellStyle: params => {
            return { background: '#f8fcd5' }
          }
        })
      }
      for (let i = 0; i < this.displayHeaders.length; ++i) {
        const header = this.displayHeaders[i]
        this.columnDefs.push({
          headerName: this.getLetterSeq(i),
          field: header.name,
          children: [
            {
              headerName: header.label,
              field: header.name,
              width: this.getColumnTextWidth(header),
              // filter: this.hasRowGroup,
              enableRowGroup: true,
              summaryMode: this.getSummaryMode(header),
              rowSpan: params => {
                if (this.hasRowGroup) {
                  return 1
                }
                if (params.data && params.data.rowSpanData && params.data.rowSpanData.hasOwnProperty(header.name)) {
                  return params.data.rowSpanData[header.name]
                }
                return 1
              },
              colSpan: params => {
                if (this.hasRowGroup) {
                  return 1
                }
                if (params.data && params.data.colSpanData && params.data.colSpanData.hasOwnProperty(header.name)) {
                  return params.data.colSpanData[header.name]
                }
                return 1
              },
              cellClass: params => {
                if (this.hasRowGroup) {
                  return ''
                }
                if (!params.data) {
                  return ''
                }
                const classArr = []
                const row = params.data
                if (row.rowSpanData && row.rowSpanData[header.name] > 1) {
                  classArr.push('row-span-class')
                }
                if ((params.data.subSum || params.data.sum) && !(row.rowSpanData && row.rowSpanData[header.name] === 0)) { // 统计
                  classArr.push('statistics-cell')
                }
                if (header.dataType === 'Double' || header.dataType === 'Integer') {
                  classArr.push('text-alige-right')
                }
                if (typeof this.cellClassName === 'function') {
                  classArr.push(this.cellClassName({ row, column: { label: header.label }}))
                }
                return classArr.join(' ')
              },
              cellStyle: params => {
                if (this.hasRowGroup) {
                  return {}
                }
                if (!params.data) {
                  return {}
                }
                const row = params.data
                if ((row.rowSpanData && row.rowSpanData[header.name] === 0) || (row.colSpanData && row.colSpanData[header.name] === 0)) {
                  return { 'borderBottom': 'none' }
                }
                return {}
              },
              comparator: (a, b, nodeA, nodeB) => {
                if (this.hasRowGroup) {
                  const isStringValue = header.dataType === 'String'
                  const isDoubleValue = header.dataType === 'Double'
                  let value1 = a
                  let value2 = b
                  if (isStringValue) {
                    if (typeof value1 === 'string' && typeof value2 === 'string') {
                      for (let i = 0; i < value1.length && i < value2.length; ++i) {
                        const aCode = value1.charCodeAt(i)
                        const bCode = value2.charCodeAt(i)
                        let diff = 0
                        if (aCode >= 256 && bCode >= 256) {
                          diff = value1[i].localeCompare(value2[i], 'zh')
                        } else {
                          diff = aCode - bCode
                        }
                        if (diff !== 0) {
                          return diff
                        }
                      }
                      if (value1.length !== value2.length) {
                        return value1.length - value2.length
                      }
                    } else {
                      if (value1 > value2) {
                        return 1
                      } else if (value1 < value2) {
                        return -1
                      }
                    }
                  } else {
                    if (isDoubleValue) {
                      if (value1 && value1.toString().lastIndexOf('%') >= 0) {
                        value1 = Number(value1.toString().substr(0, value1.toString().length - 1))
                      }
                      if (value2 && value2.toString().lastIndexOf('%') >= 0) {
                        value2 = Number(value2.toString().substr(0, value2.toString().length - 1))
                      }
                    }
                    if (value1 > value2) {
                      return 1
                    } else if (value1 < value2) {
                      return -1
                    }
                  }
                  return 0
                } else {
                  const sortItem = this.sortList.find(p => p.colId === header.name)
                  if (sortItem && nodeA.data && nodeB.data) {
                    return (nodeA.data._seq - nodeB.data._seq) * (sortItem.sort === 'asc' ? 1 : -1)
                  }
                }
                return 0
              },
              valueFormatter: params => {
                if (params.data) {
                  const row = params.data
                  if ((row.rowSpanData && row.rowSpanData[header.name] === 0) || (row.colSpanData && row.colSpanData[header.name] === 0)) {
                    return ''
                  }
                }
                return this.toFormatter(header, params.value)
              },
              onCellClicked: params => {
                if (!params.data) {
                  return
                }
                this.$emit('cellClick', params.data, header, i)
              }
            }
          ]
        })
      }
    },
    getLetterSeq(num) {
      const remainder = num % 26
      const n = (num - remainder) / 26
      if (n > 0) {
        return this.getLetterSeq(n - 1) + LETTERS[remainder]
      }
      return LETTERS[remainder]
    },
    getColumnTextWidth(header) {
      if (header.name === '_seq') {
        return 20 + String(this.finalRowData.length).length * 8
      }
      let width = this.getTextWidth(header.label) + 26
      for (const row of this.finalRowData) {
        const w = this.getTextWidth(this.toFormatter(header, row[header.name])) + 2
        if (w > width) {
          width = w
        }
      }
      return width
    },
    getTextWidth(text, fontSizePlus = 0) {
      return getLength(String(text)) * 0.5 * (16 + fontSizePlus) // 文本字节数 * 0.5 * font-size
    },
    getSummaryMode(header) {
      if (header.dataType === 'Double' || header.dataType === 'Integer') {
        return 'sum'
      }
      return 'subtotal'
    },
    toFormatter(header, value) {
      if (header.displayPrecision >= 0) {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          return formatNumberToThousands(ColumnUtils.formatNumber(value, header.displayPrecision))
        }
      }
      if (header.precision === 0) {
        return value
      }
      if (header.dataType === 'Double') {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          if (header.decimalPlaces) {
            // 后端已经处理好了小数位数，这里就不用管是否四舍五入，根据decimalPlaces，显示对应小数就行
            return formatNumberToThousands(ColumnUtils.numberOmitZeroColumnLabelFunc(value, Number(header.decimalPlaces)))
          }
          // 原仅保留两位
          return formatNumberToThousands(ColumnUtils.number2ColumnLabelFunc(value))
        }
      } else if (header.dataType === 'Integer') {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          return formatNumberToThousands(ColumnUtils.numberColumnLabelFunc(value))
        }
      }
      return value
    },
    // 进行分组编号
    toGroup() {
      for (const row of this.rowData) {
        delete row.groupNo
      }
      if (this.rowData.length < 2) {
        return
      }
      const headerMap = new Map()
      for (const header of this.displayHeaders) {
        headerMap.set(header.name, header)
      }
      for (const header of this.displayHeaders) {
        let parentGroupNo = null
        let num = 1
        let preRow = this.rowData[0]
        if (preRow.groupNo) {
          parentGroupNo = preRow.groupNo
          preRow.groupNo.push(num)
        } else {
          preRow.groupNo = [num]
        }
        for (let i = 1; i < this.rowData.length; ++i) {
          const row = this.rowData[i]
          if (row.groupNo) {
            if (this.isEqualArray(row.groupNo, parentGroupNo) && row[header.name] === preRow[header.name]) {
              row.groupNo = preRow.groupNo
            } else {
              parentGroupNo = row.groupNo
              row.groupNo.push(++num)
            }
          } else {
            if (row[header.name] === preRow[header.name]) {
              row.groupNo = preRow.groupNo
            } else {
              row.groupNo = [++num]
            }
          }
          preRow = row
        }
      }
      if (this.rowData.length > 1) {
        /* 首行处理 */
        let row = this.rowData[0]
        let nextRow = this.rowData[1]
        let commonLength = this.getArrayCommonLength(row.groupNo, nextRow.groupNo)
        if (commonLength > 1) {
          row.groupNo = row.groupNo.slice(0, commonLength).join('-')
        } else {
          row.groupNo = String(row.groupNo[0])
        }
        /* 尾行处理 */
        let preRow = this.rowData[this.rowData.length - 2]
        row = this.rowData[this.rowData.length - 1]
        commonLength = this.getArrayCommonLength(preRow.groupNo, row.groupNo)
        if (commonLength > 1) {
          row.groupNo = row.groupNo.slice(0, commonLength).join('-')
        } else {
          row.groupNo = String(row.groupNo[0])
        }
        // 剩余行处理
        for (let i = 1; i < this.rowData.length - 1; ++i) {
          preRow = this.rowData[i - 1]
          row = this.rowData[i]
          nextRow = this.rowData[i + 1]
          const commonLength1 = this.getArrayCommonLength(preRow.groupNo, row.groupNo)
          const commonLength2 = this.getArrayCommonLength(row.groupNo, nextRow.groupNo)
          const maxLen = Math.max(commonLength1, commonLength2)
          if (maxLen > 1) {
            row.groupNo = row.groupNo.slice(0, maxLen).join('-')
          } else {
            row.groupNo = String(row.groupNo[0])
          }
        }
      }
    },
    isEqualArray(a, b) {
      if (a.length !== b.length) {
        return false
      }
      for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) {
          return false
        }
      }
      return true
    },
    // 获取两个数据公共部分长度
    getArrayCommonLength(a, b) {
      if (a && b) {
        const len = Math.min(a.length, b.length)
        for (let i = 0; i < len; ++i) {
          if (a[i] !== b[i]) {
            return i
          }
        }
      }
      return 0
    },
    toStatistics() {
      this.statisticsRows = []
      /* 小计列数组 */
      const headers = []
      for (let i = 0; i < this.displayHeaders.length - 2 && i < this.subSumMaxNum; ++i) {
        if (!this.displayHeaders[i + 1].group) {
          break
        }
        headers.push(this.displayHeaders[i])
      }
      for (let i = headers.length - 1; i >= 0; --i) {
        if (!headers[i].subSum || !this.displayHeaders[i + 1].subSum) {
          headers.splice(i)
        }
      }
      if (headers.length > 0) {
        this.toSubSum(headers, this.rowData)
      }
      let sumColspan = 0
      if (headers.length > 0) {
        sumColspan = headers.length + 1
      } else {
        for (let i = 0; i < this.displayHeaders.length && i <= this.subSumMaxNum; ++i) {
          if (this.displayHeaders[i].dataType === 'Double' || this.displayHeaders[i].dataType === 'Integer') {
            break
          }
          sumColspan = i + 1
        }
      }
      // 总计
      this.toSum(sumColspan)
    },
    toSubSum(headers) {
      const displayHeaderTypes = [] // 每列的类型信息
      const map = new Map()
      for (let i = 0; i < this.rowData.length; ++i) {
        const row = this.rowData[i]
        if (i === 0) {
          for (const header of this.displayHeaders) {
            displayHeaderTypes.push(typeof row[header.name])
          }
        }
        let key
        for (const header of headers) {
          if (typeof key === 'undefined') {
            key = row[header.name]
          } else {
            key += '$' + row[header.name]
          }
          if (!header.subSum) {
            continue
          }
          const rows = map.get(key)
          if (rows) {
            rows.push(row)
          } else {
            map.set(key, [row])
          }
        }
      }
      const rows = []
      for (const [key, value] of map.entries()) {
        const keys = this.getKeysFromSplicingKey(key)
        for (let j = 0; j < keys.length; ++j) {
          if (displayHeaderTypes[j] === 'number') {
            const num = Number(keys[j])
            if (!isNaN(num)) {
              keys[j] = num
            }
          }
        }
        const row = { subSum: true, keys, subSumColspan: headers.length + 1 - keys.length }
        if (this.subSumDisiplayAll) {
          for (let j = 0; j < keys.length; ++j) {
            row[this.displayHeaders[j].name] = keys[j]
          }
        } else {
          row[this.displayHeaders[0].name] = keys[keys.length - 1]
        }
        row[this.displayHeaders[keys.length].name] = this.$t('CargoMsg.Cargo_SubTotal')
        for (let j = keys.length + 1; j < this.displayHeaders.length; ++j) {
          const header = this.displayHeaders[j]
          if (header.dataType === 'Double' || header.dataType === 'Integer') {
            row[header.name] = 0
            for (const item of value) {
              const num = Number(item[header.name])
              if (!isNaN(num)) {
                row[header.name] += num
              }
            }
          }
        }
        rows.push(row)
      }
      rows.sort(function(a, b) {
        return (a.keys.length - b.keys.length) * -1
      })
      this.statisticsRows.push(...rows)
    },
    // 从拼接的key中获取keys
    getKeysFromSplicingKey(key) {
      if (typeof key === 'string') {
        return key.split('$')
      }
      return [key]
    },
    toSum(sumColspan) {
      if (this.showSum && this.displayHeaders.length >= 2 && this.rowData.length > 0) {
        const obj = { sum: true, [this.displayHeaders[0].name]: this.$t('CargoMsg.Cargo_GrandTotal'), sumColspan }
        for (let i = 1; i < this.displayHeaders.length; ++i) {
          const header = this.displayHeaders[i]
          if (header.dataType === 'Double' || header.dataType === 'Integer') {
            obj[header.name] = 0
            for (const row of this.rowData) {
              const num = Number(row[header.name])
              if (!isNaN(num)) {
                obj[header.name] += num
              }
            }
          }
        }
        this.statisticsRows.push(obj)
      }
    },
    onSortChanged(sortList) {
      this.sortedRowData = lodash.cloneDeep(this.rowData)
      if (sortList) {
        this.sortList = sortList
        this.sortHeaders = []
        this.sortTypes = []
        this.sortHeader = null
        this.sortType = ''
        if (sortList.length > 0) {
          for (let i = 0; i < sortList.length; ++i) {
            const sortItem = sortList[i]
            const sortHeader = this.displayHeaders.find(p => p.name === sortItem.colId)
            const sortType = sortItem.sort === 'desc' ? 'descending' : 'ascending'
            this.sortHeaders.push(sortHeader)
            this.sortTypes.push(sortType)
            if (i === 0) {
              this.sortHeader = sortHeader
              this.sortType = sortType
            }
          }
          this.toOrderBy(this.sortedRowData, sortList)
        }
      }
      for (let i = this.sortedRowData.length - 1; i >= 0; --i) {
        const row = this.sortedRowData[i]
        if (row.subSum || row.sum) {
          this.sortedRowData.splice(i, 1)
        }
      }
      if (this.rowData.length > 2) {
        const arr = []
        const map = new Map()
        for (const row of this.sortedRowData) {
          let subArr = map.get(row.groupNo)
          if (!subArr) {
            subArr = []
            arr.push(subArr)
            map.set(row.groupNo, subArr)
          }
          subArr.push(row)
        }
        const newSortedRowData = []
        for (const subArr of arr) {
          newSortedRowData.push(...subArr)
        }
        this.sortedRowData = newSortedRowData
      }
      let sumRow = null
      for (const row of this.statisticsRows) {
        if (row.subSum) {
          let j = this.sortedRowData.length - 1
          let isMatch = false
          for (; j >= 0; --j) {
            const sortedRow = this.sortedRowData[j]
            let k = 0
            for (; k < row.keys.length; ++k) {
              const sortRowValue = sortedRow.subSum && k < sortedRow.keys.length ? sortedRow.keys[k] : sortedRow[this.displayHeaders[k].name]
              if (typeof row.keys[k] !== 'number' && typeof sortRowValue === 'number') {
                row.keys[k] = Number(row.keys[k])
                row[this.displayHeaders[k].name] = row.keys[k]
              }
              if (row.keys[k] !== sortRowValue) {
                break
              }
            }
            if (k === row.keys.length) {
              isMatch = true
              break
            }
          }
          if (isMatch) {
            this.sortedRowData.splice(j + 1, 0, row)
          }
        } else if (row.sum) {
          sumRow = row
        }
      }
      if (sumRow) {
        this.sortedRowData.push(sumRow)
      }
      for (let i = 0; i < this.sortedRowData.length; ++i) {
        this.sortedRowData[i]._seq = i + 1
      }
      this.createSpanData()
    },
    toOrderBy(data, sortList) {
      const headerMap = new Map()
      for (const header of this.displayHeaders) {
        headerMap.set(header.name, header)
      }
      data.sort(function(a, b) {
        for (const sortItem of sortList) {
          const header = headerMap.get(sortItem.colId)
          if (!header) {
            continue
          }
          const sign = sortItem.sort === 'asc' ? 1 : '-1'
          const isAsc = sortItem.sort === 'asc'
          const isStringValue = header.dataType === 'String'
          const isDoubleValue = header.dataType === 'Double'
          let value1 = a[sortItem.colId]
          let value2 = b[sortItem.colId]
          if (isStringValue) {
            if (typeof value1 === 'string' && typeof value2 === 'string') {
              for (let i = 0; i < value1.length && i < value2.length; ++i) {
                const aCode = value1.charCodeAt(i)
                const bCode = value2.charCodeAt(i)
                let diff = 0
                if (aCode >= 256 && bCode >= 256) {
                  if (isAsc) {
                    diff = value1[i].localeCompare(value2[i], 'zh')
                  } else {
                    diff = value2[i].localeCompare(value1[i], 'zh')
                  }
                } else {
                  if (isAsc) {
                    diff = aCode - bCode
                  } else {
                    diff = bCode - aCode
                  }
                }
                if (diff !== 0) {
                  return diff
                }
              }
              if (value1.length !== value2.length) {
                return isAsc ? value1.length - value2.length : value2.length - value1.length
              }
            } else {
              if (value1 > value2) {
                return 1 * sign
              } else if (value1 < value2) {
                return -1 * sign
              }
            }
          } else {
            if (isDoubleValue) {
              if (value1 && value1.toString().lastIndexOf('%') >= 0) {
                value1 = Number(value1.toString().substr(0, value1.toString().length - 1))
              }
              if (value2 && value2.toString().lastIndexOf('%') >= 0) {
                value2 = Number(value2.toString().substr(0, value2.toString().length - 1))
              }
            }
            if (value1 > value2) {
              return 1 * sign
            } else if (value1 < value2) {
              return -1 * sign
            }
          }
        }
        return 0
      })
    },
    createSpanData() {
      const headers = this.displayHeaders
      for (let rowIndex = 0; rowIndex < this.sortedRowData.length; ++rowIndex) {
        const row = this.sortedRowData[rowIndex]
        row.colSpanData = row.colSpanData || {}
        if (row.subSum || row.sum) {
          const colSpanData = row.colSpanData
          let spaceCloumnStartIndex
          if (row.subSum) {
            const keys = row.keys
            const subSumColumnIndex = keys.length
            if (!this.subSumDisiplayAll) {
              if (keys.length > 1) {
                colSpanData[headers[0].name] = keys.length
              }
              for (let i = 1; i < subSumColumnIndex; ++i) {
                colSpanData[headers[i].name] = 0
              }
            }
            colSpanData[headers[subSumColumnIndex].name] = row.subSumColspan
            for (let j = subSumColumnIndex + 1; j < subSumColumnIndex + row.subSumColspan; ++j) {
              colSpanData[headers[j].name] = 0
            }
            spaceCloumnStartIndex = subSumColumnIndex + row.subSumColspan
            let spaceColspan = 0
            let i = spaceCloumnStartIndex
            for (; i < headers.length; ++i) {
              if (row.hasOwnProperty(headers[i].name)) {
                break
              } else {
                spaceColspan += 1
                colSpanData[headers[i].name] = 0
              }
            }
            colSpanData[headers[spaceCloumnStartIndex].name] = spaceColspan
          } else {
            colSpanData[headers[0].name] = row.sumColspan
            for (let i = 1; i < row.sumColspan; ++i) {
              colSpanData[headers[i].name] = 0
            }
            spaceCloumnStartIndex = row.sumColspan
            let spaceColspan = 0
            let i = spaceCloumnStartIndex
            for (; i < headers.length; ++i) {
              if (row.hasOwnProperty(headers[i].name)) {
                break
              } else {
                spaceColspan += 1
                colSpanData[headers[i].name] = 0
              }
            }
            if (spaceColspan > 0) {
              colSpanData[headers[spaceCloumnStartIndex].name] = spaceColspan
            }
          }
        }
      }
      for (let headerIndex = 0; headerIndex < headers.length; ++headerIndex) {
        const header = headers[headerIndex]
        for (let rowIndex = 0; rowIndex < this.sortedRowData.length; ++rowIndex) {
          const row = this.sortedRowData[rowIndex]
          row.rowSpanData = row.rowSpanData || {}
          if (row.rowSpanData.hasOwnProperty(header.name)) {
            continue
          }
          const value = row[header.name]
          let rowspan = 1
          for (let i = rowIndex + 1; i < this.sortedRowData.length; ++i) {
            const otherRow = this.sortedRowData[i]
            let isCrossRow = false
            if (otherRow[header.name] === value) {
              if (headerIndex > 0) {
                if (otherRow.rowSpanData) {
                  const preColspan = otherRow.rowSpanData[headers[headerIndex - 1].name]
                  if (preColspan === 0) {
                    isCrossRow = true
                  }
                }
              } else {
                isCrossRow = true
              }
            }
            if (isCrossRow) {
              rowspan += 1
              if (!otherRow.rowSpanData) {
                otherRow.rowSpanData = {}
              }
              otherRow.rowSpanData[header.name] = 0
            } else {
              break
            }
          }
          row.rowSpanData[header.name] = rowspan
        }
      }
    },
    onColumnRowGroupChanged(event) {
      const columns = event.columns
      if (columns && columns.length > 0) {
        if (!this.hasRowGroup) {
          this.$nextTick(() => {
            this.resetColumnDefs()
          })
        }
        this.hasRowGroup = true
      } else {
        if (this.hasRowGroup) {
          this.$nextTick(() => {
            this.resetColumnDefs()
          })
        }
        this.hasRowGroup = false
      }
    },
    getAllDisplayedTitle() {
      return Array.from(this.gridOptions.columnApi.getAllDisplayedColumns(), p => p.colDef.headerName).filter(p => p)
    }
  }
}
</script>

<style lang="scss" scoped>
.dw-ag-grid::v-deep {
  width: 100%;
  height: 100%;
  &.loanding-renderer {
    .ag-body-viewport {
      // background: url('/static/mouse_wait.gif') no-repeat;
      // background-size: 22px 22px;
      // background-position: center;
    }
  }
  .ag-header-row-column-group, .ag-header-yellow {
    background: #f8fcd5;
  }
  .ag-row {
    background-color: white;
    &.ag-row-hover, &.ag-row-focus {
      background-color: #F1F3F7 !important;
      .row-span-class, .statistics-cell {
        background: #F1F3F7 !important;
      }
    }
  }
  .ag-cell {
    border-right: 1px solid #DBDFE6;
    border-bottom: 1px solid #DBDFE6;
  }
  .row-span-class {
    display: flex;
    text-align: center;
    align-items: center;
    word-wrap: break-word;
  }
  .statistics-cell {
    background: #f8fcd5;
  }
  .text-alige-right {
    text-align: right;
  }
}
</style>
